import {CommonModule} from '@angular/common';
import {Component, Inject} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {EmTranslatePipe} from '@emma-labs/shared/angular';
import {
  copyToClipboard,
  ENVIRONMENT,
  EnvironmentBroker,
  ID_DOCUMENT_TYPE_OPTIONS,
  IdDocument,
  IdDocumentType,
  NON_ALPHANUMERIC_REGEX,
} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ButtonModule} from 'primeng/button';
import {CardModule} from 'primeng/card';
import {DropdownModule} from 'primeng/dropdown';
import {InputTextModule} from 'primeng/inputtext';
import {debounceTime, filter} from 'rxjs';
import {distinctUntilChanged} from 'rxjs/operators';
import {DEBOUNCE_TIME} from '../../../utils/utility-constants';
import {BaseSectionComponent} from '../../base-section/base-section.component';

interface IdValidationForm {
  type: FormControl<IdDocumentType | null>;
  code: FormControl<string | null>;
}

@UntilDestroy()
@Component({
  selector: 'em-id-document-section',
  templateUrl: './id-document-section.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    ButtonModule,
    DropdownModule,
    InputTextModule,
    EmTranslatePipe,
  ],
})
export class IdDocumentSectionComponent extends BaseSectionComponent<
  FormGroup<IdValidationForm>,
  IdDocument
> {
  readonly documentTypesOptions = ID_DOCUMENT_TYPE_OPTIONS;

  constructor(
    private readonly fb: FormBuilder,
    @Inject(ENVIRONMENT) environment: EnvironmentBroker,
  ) {
    super(environment);
    this.form = this.createForm();
  }

  simulateIdDocument(): void {
    this.form.setValue({
      type:
        Math.random() < 0.33
          ? IdDocumentType.PASSPORT
          : Math.random() < 0.5
            ? IdDocumentType.HEALTH_INSURANCE
            : IdDocumentType.DRIVER_LICENSE,
      code: crypto.randomUUID().split('-')[0],
    });

    if (this.form.controls.code.value) {
      const cleanedConfirmedIdDocumentCode = this.form.controls.code.value
        .replace(new RegExp(NON_ALPHANUMERIC_REGEX, 'g'), '')
        .toLowerCase();
      copyToClipboard(cleanedConfirmedIdDocumentCode.slice(-4));
    }
  }

  private createForm(): FormGroup<IdValidationForm> {
    const form = this.fb.group<IdValidationForm>({
      type: this.fb.control(null, [Validators.required]),
      code: this.fb.control(null, [Validators.required, Validators.minLength(5), Validators.maxLength(15)]),
    });

    form.valueChanges
      .pipe(
        filter(() => this.form.valid),
        distinctUntilChanged(),
        debounceTime(DEBOUNCE_TIME),
        untilDestroyed(this),
      )
      .subscribe((_) => {
        this.propsChange.emit({output: this.formToOutput()});
      });

    return form;
  }
}
